import {
  DufBulkImportGeometryEntityDto,
  DufLayerRefDto
} from 'providers/api';
import { volumeImperialToMetric } from 'utils/unitConversions';
import { ClippingBounds } from '../../MineModel/clippingBounds';
import {
  AccessType,
  AssignmentResult,
  SearchBox,
  TOP_ACCESS_DEFAULT_SEARCH_BOX,
  defaultSearchBoxForAccess,
  AssignmentStatus
} from './autoAssignUtils';

export type ImportMode = 'skip' | 'update_shapefile' | 'full_import';
export type SolidAction = 'pending' | 'mark_as_stope' | 'project_to_stope' | 'insert_between_nodes';

export type WizardPhase =
  | 'upload'
  | 'access_type'
  | 'search_params'
  | 'processing'
  | 'summary'
  | 'manual_review'
  | 'done';

export type ExistingStopeBehavior = 'update' | 'skip';

export interface SolidDecision {
  entityGuid: string;
  action: SolidAction;
  existingNodeId?: string;
  existingStopeId?: string;
  newNodePosition?: { x: number; y: number; z: number };
  insertBetweenNodes?: { nodeAId: string; nodeBId: string };
  spoolFromNodeId?: string;
  spoolPipeTypeId?: string;
  stopeName?: string;
  volume?: number;
  startDate?: string;
  endDate?: string;
  importMode?: ImportMode;
  saved: boolean;
}

let insertedNodeCounter = 0;

export const getNextInsertedNodeName = (existingIds: Set<string> | string[] = []): string => {
  const reserved = existingIds instanceof Set ? existingIds : new Set(existingIds);
  let next: string;
  do {
    insertedNodeCounter += 1;
    next = `imported node_${String(insertedNodeCounter).padStart(3, '0')}`;
  } while (reserved.has(next));
  return next;
};

export interface SpoolClickInfo {
  spoolId: string;
  nodeAId: string;
  nodeBId: string;
  clickPoint: { x: number; y: number; z: number };
}

export const initClippingBoundsForSolid = (solid: DufBulkImportGeometryEntityDto): ClippingBounds | null => {
  const bb = solid.boundingBox;
  if (!bb) return null;
  const sizeX = (bb.maxX ?? 0) - (bb.minX ?? 0);
  const sizeY = (bb.maxY ?? 0) - (bb.minY ?? 0);
  const sizeZ = (bb.maxZ ?? 0) - (bb.minZ ?? 0);
  const expansion = Math.max(sizeX, sizeY, sizeZ) * 0.5;
  return {
    minX: (bb.minX ?? 0) - expansion,
    maxX: (bb.maxX ?? 0) + expansion,
    minY: (bb.minY ?? 0) - expansion,
    maxY: (bb.maxY ?? 0) + expansion,
    minZ: (bb.minZ ?? 0) - expansion,
    maxZ: (bb.maxZ ?? 0) + expansion,
  };
};

export interface BulkImportSolidsSceneState {
  wizardPhase: WizardPhase;
  solids: DufBulkImportGeometryEntityDto[];
  layers: DufLayerRefDto[];
  // The geometry on `solids` is already metric; this only flags the raw DUF attributes, which
  // are still in file units and so need converting before they are persisted.
  imperialUnits: boolean;
  selectedLayerGuid: string;
  selectedNameAttribute: string;
  renameDuplicateNames: boolean;
  /** Names given to solids that share a name, keyed by solid guid; set when the review starts. */
  solidNameOverrides: Record<string, string>;
  selectedStartDateAttribute: string;
  selectedEndDateAttribute: string;
  selectedVolumeAttribute: string;
  selectedNodeId: string | undefined;
  projectedNodePosition: { x: number; y: number; z: number } | null;
  spoolClickInfo: SpoolClickInfo | null;
  isInsertingNode: boolean;
  isSelectingSpoolForInsert: boolean;
  decisions: Record<string, SolidDecision>;
  computedMeshVolume: number | null;
  isParsing: boolean;
  isSaving: boolean;
  error: string | null;
  version: number;
  accessType: AccessType;
  searchBox: SearchBox;
  existingStopeBehavior: ExistingStopeBehavior;
  autoAssignResults: AssignmentResult[];
  manualReviewQueue: string[];
  currentManualReviewIndex: number;
  processingProgress: number;
}

const defaultState: BulkImportSolidsSceneState = {
  wizardPhase: 'upload',
  solids: [],
  layers: [],
  imperialUnits: false,
  selectedLayerGuid: '',
  selectedNameAttribute: '',
  renameDuplicateNames: false,
  solidNameOverrides: {},
  selectedStartDateAttribute: '',
  selectedEndDateAttribute: '',
  selectedVolumeAttribute: '',
  selectedNodeId: undefined,
  projectedNodePosition: null,
  spoolClickInfo: null,
  isInsertingNode: false,
  isSelectingSpoolForInsert: false,
  decisions: {},
  computedMeshVolume: null,
  isParsing: false,
  isSaving: false,
  error: null,
  version: 0,
  accessType: 'top',
  searchBox: TOP_ACCESS_DEFAULT_SEARCH_BOX,
  existingStopeBehavior: 'skip',
  autoAssignResults: [],
  manualReviewQueue: [],
  currentManualReviewIndex: 0,
  processingProgress: 0,
};

let sceneState: BulkImportSolidsSceneState = { ...defaultState };

const stateListeners: Set<() => void> = new Set();

export const getBulkImportSolidsSceneState = () => sceneState;

export const updateBulkImportSolidsSceneState = (updates: Partial<BulkImportSolidsSceneState>) => {
  sceneState = { ...sceneState, ...updates, version: sceneState.version + 1 };
  stateListeners.forEach((listener) => listener());
};

export const subscribeToBulkImportSolidsSceneState = (listener: () => void): (() => void) => {
  stateListeners.add(listener);
  return () => { stateListeners.delete(listener); };
};

export const resetBulkImportSolidsSceneState = () => {
  sceneState = { ...defaultState, version: 0 };
  insertedNodeCounter = 0;
  stateListeners.forEach((listener) => listener());
};

export const getCurrentSolid = (): DufBulkImportGeometryEntityDto | undefined => {
  const {
    solids,
    wizardPhase,
    manualReviewQueue,
    currentManualReviewIndex,
  } = sceneState;
  if (wizardPhase !== 'manual_review') return undefined;
  const guid = manualReviewQueue[currentManualReviewIndex];
  if (!guid) return undefined;
  return solids.find((s) => s.guid === guid);
};

export const getAttributeSolidName = (solid: DufBulkImportGeometryEntityDto): string => {
  const { selectedNameAttribute } = sceneState;
  if (!selectedNameAttribute || !solid.attributes) return solid.guid ?? 'Unknown';
  return solid.attributes[selectedNameAttribute] ?? solid.guid ?? 'Unknown';
};

export const getSolidName = (solid: DufBulkImportGeometryEntityDto): string => (
  (solid.guid && sceneState.solidNameOverrides[solid.guid]) || getAttributeSolidName(solid)
);

export const getSolidStartDate = (solid: DufBulkImportGeometryEntityDto): string | undefined => {
  const { selectedStartDateAttribute } = sceneState;
  if (!selectedStartDateAttribute || !solid.attributes) return undefined;
  return solid.attributes[selectedStartDateAttribute];
};

export const getSolidEndDate = (solid: DufBulkImportGeometryEntityDto): string | undefined => {
  const { selectedEndDateAttribute } = sceneState;
  if (!selectedEndDateAttribute || !solid.attributes) return undefined;
  return solid.attributes[selectedEndDateAttribute];
};

export const getSolidVolumeAttribute = (solid: DufBulkImportGeometryEntityDto): number | undefined => {
  const { selectedVolumeAttribute, imperialUnits } = sceneState;
  if (!selectedVolumeAttribute || !solid.attributes) return undefined;
  const val = parseFloat(solid.attributes[selectedVolumeAttribute]);
  if (Number.isNaN(val)) return undefined;
  return imperialUnits ? volumeImperialToMetric(val) : val;
};

/**
 * Without this the decision keeps whatever was selected when the action button was pressed, which is
 * nothing when no node had been picked yet.
 */
export const decisionsWithNodeApplied = (nodeId: string): Pick<BulkImportSolidsSceneState, 'decisions'> | Record<string, never> => {
  const solid = getCurrentSolid();
  const existingDecision = solid?.guid ? sceneState.decisions[solid.guid] : undefined;
  if (!existingDecision || !solid?.guid) return {};
  if (existingDecision.action === 'mark_as_stope') {
    return { decisions: { ...sceneState.decisions, [solid.guid]: { ...existingDecision, existingNodeId: nodeId } } };
  }
  if (existingDecision.action === 'project_to_stope') {
    return { decisions: { ...sceneState.decisions, [solid.guid]: { ...existingDecision, spoolFromNodeId: nodeId } } };
  }
  return {};
};

export const isManualReviewResolved = (guid: string): boolean => {
  const decision = sceneState.decisions[guid];
  return !!decision && (decision.importMode === 'skip' || decision.action !== 'pending');
};

export const firstUnresolvedManualReviewIndex = (): number => {
  const index = sceneState.manualReviewQueue.findIndex((guid) => !isManualReviewResolved(guid));
  return index === -1 ? 0 : index;
};

export const countResolvedManualReviews = (): number => (
  sceneState.manualReviewQueue.filter(isManualReviewResolved).length
);

export const setWizardPhase = (wizardPhase: WizardPhase) => {
  updateBulkImportSolidsSceneState({ wizardPhase });
};

export const setAccessType = (accessType: AccessType) => {
  updateBulkImportSolidsSceneState({
    accessType,
    searchBox: defaultSearchBoxForAccess(accessType),
  });
};

export const setSearchBox = (searchBox: SearchBox) => {
  updateBulkImportSolidsSceneState({ searchBox });
};

export const setExistingStopeBehavior = (existingStopeBehavior: ExistingStopeBehavior) => {
  updateBulkImportSolidsSceneState({ existingStopeBehavior });
};

export const setRenameDuplicateNames = (renameDuplicateNames: boolean) => {
  updateBulkImportSolidsSceneState({ renameDuplicateNames });
};

export const setProcessingProgress = (processingProgress: number) => {
  updateBulkImportSolidsSceneState({ processingProgress });
};

export type AutoAssignDecisionAugmenter = (solidGuid: string) => Partial<SolidDecision>;

const decisionsFromAutoAssignResults = (
  results: AssignmentResult[],
  previous: Record<string, SolidDecision>,
  augment: AutoAssignDecisionAugmenter,
): Record<string, SolidDecision> => results.reduce<Record<string, SolidDecision>>((acc, result) => {
  const existing = previous[result.solidGuid];
  const base: SolidDecision = existing ?? {
    entityGuid: result.solidGuid,
    action: 'pending',
    saved: false,
  };
  const extras = augment(result.solidGuid);
  if (result.status === 'mark_existing_node') {
    acc[result.solidGuid] = {
      ...base,
      ...extras,
      action: 'mark_as_stope',
      existingNodeId: result.existingNodeId,
      importMode: 'full_import',
    };
  } else if (result.status === 'insert_between_nodes') {
    acc[result.solidGuid] = {
      ...base,
      ...extras,
      action: 'insert_between_nodes',
      newNodePosition: result.newNodePosition,
      insertBetweenNodes: result.nodeAId && result.nodeBId
        ? { nodeAId: result.nodeAId, nodeBId: result.nodeBId }
        : undefined,
      importMode: 'full_import',
    };
  } else if (result.status === 'update_existing') {
    acc[result.solidGuid] = {
      ...base,
      ...extras,
      action: 'mark_as_stope',
      existingStopeId: result.existingStopeId,
      importMode: 'update_shapefile',
    };
  } else if (result.status === 'skipped_existing') {
    acc[result.solidGuid] = {
      ...base,
      action: 'pending',
      importMode: 'skip',
    };
  }
  return acc;
}, { ...previous });

export const setAutoAssignResults = (
  results: AssignmentResult[],
  augment: AutoAssignDecisionAugmenter,
) => {
  const manualReviewQueue = results
    .filter((r) => r.status === 'manual_review')
    .map((r) => r.solidGuid);

  const decisions = decisionsFromAutoAssignResults(results, sceneState.decisions, augment);

  updateBulkImportSolidsSceneState({
    autoAssignResults: results,
    manualReviewQueue,
    currentManualReviewIndex: 0,
    decisions,
  });
};

export type AutoAssignStatusCounts = Record<AssignmentStatus, number>;

export const countAssignmentResults = (results: AssignmentResult[]): AutoAssignStatusCounts => (
  results.reduce<AutoAssignStatusCounts>((acc, r) => {
    acc[r.status] = (acc[r.status] ?? 0) + 1;
    return acc;
  }, {
    mark_existing_node: 0,
    insert_between_nodes: 0,
    update_existing: 0,
    manual_review: 0,
    skipped_existing: 0,
  })
);
